import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment-jalaali';
import axios from '../api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

function formatPersianTimestamp(timestamp) {
  const now = moment();
  const time = moment(timestamp);

  let formatted;
  if (now.isSame(time, 'day')) {
    formatted = time.format('HH:mm');
  } else if (now.isSame(time, 'year')) {
    formatted = time.format('jM/jD HH:mm'); // Month before Day
  } else {
    formatted = time.format('jYYYY/jM/jD HH:mm');
  }

  // Convert to Farsi digits
  return formatted.replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
}

function ChatSection({ ad, currentUser }) {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [pollInterval, setPollInterval] = useState(null);
  const messagesEndRef = useRef(null);
  const prevMessagesLength = useRef(0);

  // Fetch only chats for this ad and user
  const fetchChats = async () => {
    if (!ad || !currentUser) return;
    const res = await axios.get(`/chats/?ad_id=${ad.id}`);
    setChats(res.data);
  };

  const fetchMessages = async (chatId) => {
    if (!chatId) return;
    const res = await axios.get(`/messages/?chat_id=${chatId}`);
    setMessages(res.data);
  };

  useEffect(() => {
    if (!ad || !currentUser) return;
    if (ad.user.id === currentUser.id) {
      fetchChats();
    } else {
      // Always create/fetch chat for this ad and user pair only
      axios.post('/chats/get_or_create_chat/', {
        ad_id: ad.id,
        other_user_id: currentUser.id,
      })
      .then(res => {
        setSelectedChat(res.data);
      })
      .catch(err => {
        // Optionally handle error
      });
    }
    // eslint-disable-next-line
  }, [ad, currentUser]);

  useEffect(() => {
    if (!selectedChat) return;
    fetchMessages(selectedChat.id);
  }, [selectedChat]);

  useEffect(() => {
    if (!selectedChat) return;
    if (pollInterval) clearInterval(pollInterval);
    const interval = setInterval(() => fetchMessages(selectedChat.id), 5000);
    setPollInterval(interval);
    return () => clearInterval(interval);
  }, [selectedChat]);

  useEffect(() => {
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [pollInterval]);

  useEffect(() => {
    if (messagesEndRef.current && messages.length > prevMessagesLength.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
    prevMessagesLength.current = messages.length;
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    await axios.post('/messages/', { chat: selectedChat.id, content: newMessage });
    setNewMessage('');
    fetchMessages(selectedChat.id);
  };

  if (!currentUser) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">برای ارسال پیام ابتدا وارد شوید.</Typography>
      </Box>
    );
  }

  if (!ad || !currentUser) return null;

  const renderMessages = () => (
    <Box
      sx={{
        maxHeight: 320,
        overflowY: 'auto',
        bgcolor: '#f7f7fa',
        p: 2,
        borderRadius: 3,
        mb: 2,
        border: '1px solid #eee'
      }}
      ref={messagesEndRef}
    >
      {messages.length > 0 ? messages.map(msg => (
        <Box
          key={msg.id}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: msg.sender.id === currentUser.id ? 'flex-end' : 'flex-start',
            mb: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: msg.sender.id === currentUser.id ? 'primary.main' : 'grey.200',
              color: msg.sender.id === currentUser.id ? 'white' : 'black',
              px: 2,
              py: 1,
              borderRadius: 3,
              maxWidth: '80%',
              boxShadow: 1,
              ml: msg.sender.id === currentUser.id ? 'auto' : 0,
              mr: msg.sender.id === currentUser.id ? 0 : 'auto',
            }}
          >
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{msg.content}</Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 0.5, fontSize: 11, textAlign: msg.sender.id === currentUser.id ? 'right' : 'left', width: '100%' }}
          >
            {formatPersianTimestamp(msg.timestamp)}
          </Typography>
        </Box>
      )) : <Typography variant="body2" color="text.secondary">هنوز پیامی وجود ندارد.</Typography>}
    </Box>
  );

  const renderInput = () => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
      <TextField
        value={newMessage}
        onChange={e => setNewMessage(e.target.value)}
        size="small"
        placeholder="پیام خود را بنویسید..."
        sx={{ flex: 1, bgcolor: 'white', borderRadius: 2 }}
      />
      <Button variant="contained" onClick={sendMessage} sx={{ minWidth: 80, borderRadius: 2 }}>ارسال</Button>
    </Box>
  );

  if (ad.user.id === currentUser.id) {
    return (
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: 400, bgcolor: 'background.paper', borderRadius: 4, boxShadow: 4, p: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom align="center">پیام‌های دریافتی</Typography>
        <List sx={{ mb: 2 }}>
        {chats.map(chat => {
          let displayName = '';
          if (chat.user2 && chat.user2.name && chat.user2.name.trim() !== '') {
          displayName = chat.user2.name;
          } else if (chat.user2 && chat.user2.id) {
          // Convert the number to Farsi digits
          const farsiNumber = (chat.user2.id * 1654).toString().replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
            displayName = `کاربر-${farsiNumber}`;
          } else {
          displayName = 'کاربر';
          }
          return (
          <ListItem
            button
            key={chat.id}
            selected={selectedChat && selectedChat.id === chat.id}
            onClick={() => setSelectedChat(chat)}
            sx={{
            borderRadius: 2,
            mb: 1,
            bgcolor: selectedChat && selectedChat.id === chat.id ? 'primary.50' : 'inherit'
            }}
          >
            <ListItemText
              primary={displayName}
              secondary={(() => {
                const m = moment(chat.created_at);
                let date = m.format('jYYYY/jM/jD');
                let time = m.format('HH:mm');
                date = date.replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
                time = time.replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
                return `${time}   ${date}`;
              })()}
              secondaryTypographyProps={{ fontSize: '0.7em', color: '#a1a1a1ff' }}
            />
          </ListItem>
          );
        })}
        </List>
        {chats.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          هنوز پیامی برای این آگهی ارسال نشده است
        </Typography>
        ) : (
        selectedChat ? (
          <>
          <Typography variant="subtitle1" fontWeight={600} align="center">
            گفتگو با {
            selectedChat.user2 && selectedChat.user2.name && selectedChat.user2.name.trim() !== ''
              ? selectedChat.user2.name
              : (selectedChat.user2 && selectedChat.user2.id
                ? `کاربر-${(selectedChat.user2.id * 1654).toString().replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])}`
                : (selectedChat.user2 && selectedChat.user2.username ? selectedChat.user2.username : 'کاربر')
              )
            }
          </Typography>
          {renderMessages()}
          {renderInput()}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          برای مشاهده و ارسال پیام، یک گفتگو را انتخاب کنید.
          </Typography>
        )
        )}
      </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: 400, bgcolor: 'background.paper', borderRadius: 4, boxShadow: 4, p: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom align="center">گفتگو با فروشنده</Typography>
        {renderMessages()}
        {renderInput()}
      </Box>
    </Box>
  );
}

export default ChatSection;
